// @flow

import { decodeObject } from "../../shared/utils/decode";
import { InternalOnlyError } from "../../shared/utils/errors";
import { initializeDatePicker } from "./initializeDatePicker";

// The air-datepicker library ships no Flow types, so its instance type stays open.
// $FlowFixMe[unclear-type]
type AirDatepicker = any;

type DatePickerOnSelectData = {|
  inputId: string,
  datePicker: AirDatepicker,
  hiddenInputElement: HTMLInputElement,
  clearButtonElement: ?HTMLElement,
  shouldHideClearButton: boolean,
|};

type DatePickerMinMaxData = {|
  datePickerWithBoundary: AirDatepicker,
  type: "maxDate" | "minDate",
|};

const bindDatePicker = (
  hiddenInputElement: HTMLInputElement,
  inputElement: HTMLInputElement,
  clearButtonElement: ?HTMLElement,
  calendarButtonElement: ?HTMLElement,
  shouldHideClearButton: boolean
): AirDatepicker => {
  // We don't bind the OnSelect behaviour to the date picker initialization, because to support fields like
  // StartDateField and EndDateField that adjust each other's behaviour, we need to add it later.
  const datePicker = initializeDatePicker(inputElement);

  if (hiddenInputElement.value) {
    datePicker.selectDate(hiddenInputElement.value);
  }

  if (clearButtonElement) {
    clearButtonElement.addEventListener("click", () => datePicker.clear());

    if (shouldHideClearButton || datePicker.selectedDates.length <= 0) {
      clearButtonElement.classList.add("hidden");
    }
  }

  calendarButtonElement?.addEventListener("click", () => inputElement.focus());
  return datePicker;
};

const addOnSelectBehaviourToDatePickers = (
  datePickerDataList: Array<DatePickerOnSelectData>,
  inputIdToDatePickersWithMinMaxBoundary: Map<string, Array<DatePickerMinMaxData>>
) => {
  datePickerDataList.forEach((datePickerData) => {
    const { inputId, datePicker, hiddenInputElement, clearButtonElement, shouldHideClearButton } = datePickerData;
    datePicker.update({
      onSelect: (datePickerProps) => {
        const { date } = datePickerProps;

        hiddenInputElement.value = date ? date.toISOString().split("T")[0] : "";

        if (!shouldHideClearButton && date) {
          clearButtonElement?.classList.remove("hidden");
        } else {
          clearButtonElement?.classList.add("hidden");
        }

        // If any other date pickers change their bounds when a value is selected in this date picker, we update them
        // here
        if (inputIdToDatePickersWithMinMaxBoundary.has(inputId)) {
          const datePickersToUpdate = inputIdToDatePickersWithMinMaxBoundary.get(inputId);
          datePickersToUpdate?.forEach(({ datePickerWithBoundary, type }) =>
            datePickerWithBoundary.update({ [type as string]: date || false })
          );
        }
      },
    });
  });
};

const updateInputIdToDatePickersWithMinMaxBoundaryMap = (
  inputIdToDatePickerMap: Map<string, Array<DatePickerMinMaxData>>,
  inputId: string,
  type: "maxDate" | "minDate",
  datePickerWithBoundary: AirDatepicker
) => {
  const datePickers: Array<DatePickerMinMaxData> = inputIdToDatePickerMap.get(inputId) ?? [];
  datePickers.push({ datePickerWithBoundary, type });
  inputIdToDatePickerMap.set(inputId, datePickers);
};

const initializeDatePickerContainers = () => {
  const instantiatedDatePickers: Array<DatePickerOnSelectData> = [];
  const datePickersWithMinMaxBoundary: Map<string, Array<DatePickerMinMaxData>> = new Map();

  document.querySelectorAll(".date-picker-input-container").forEach((container) => {
    if (!(container instanceof HTMLElement)) {
      return;
    }
    const {
      inputId,
      inputHideClearButton,
      inputMinDateCalendarId,
      inputMaxDateCalendarId,
      inputErrors,
      inputDisabled,
    } = container.dataset;

    const hiddenInputElement = document.getElementById(`${inputId}`);
    const inputElementDisplayId = `${inputId}_display`;
    const inputElement = document.getElementById(inputElementDisplayId);
    const clearButtonMatch = container.querySelector(".date-picker-input__close-button");
    const calendarButtonMatch = container.querySelector(".date-picker-input__button");
    const clearButton = clearButtonMatch instanceof HTMLElement ? clearButtonMatch : null;
    const calendarButton = calendarButtonMatch instanceof HTMLElement ? calendarButtonMatch : null;

    if (inputDisabled) {
      const isDisabled = decodeObject(inputDisabled);
      if (isDisabled) {
        if (hiddenInputElement instanceof HTMLInputElement) {
          hiddenInputElement.disabled = true;
        }
        if (inputElement instanceof HTMLInputElement) {
          inputElement.disabled = true;
        }
        container.classList.add("is-disabled");
        return;
      }
    }

    if (!(hiddenInputElement instanceof HTMLInputElement) || !(inputElement instanceof HTMLInputElement)) {
      throw new InternalOnlyError("Calendar input not found!");
    }

    const shouldHideClearButton = String(inputHideClearButton) === "True";
    const datePicker = bindDatePicker(
      hiddenInputElement,
      inputElement,
      clearButton,
      calendarButton,
      shouldHideClearButton
    );

    if (inputMinDateCalendarId) {
      updateInputIdToDatePickersWithMinMaxBoundaryMap(
        datePickersWithMinMaxBoundary,
        inputMinDateCalendarId,
        "minDate",
        datePicker
      );
    }
    if (inputMaxDateCalendarId) {
      updateInputIdToDatePickersWithMinMaxBoundaryMap(
        datePickersWithMinMaxBoundary,
        inputMaxDateCalendarId,
        "maxDate",
        datePicker
      );
    }
    instantiatedDatePickers.push({
      datePicker,
      hiddenInputElement,
      shouldHideClearButton,
      inputId: inputElementDisplayId,
      clearButtonElement: clearButton,
    });

    const errors: Array<string> = decodeObject(inputErrors);
    errors.forEach((error: string) => {
      const errorElement = document.createElement("p");

      errorElement.textContent = error;
      errorElement.classList.add("help", "is-danger", "is-size-6", "mb-2");

      container.appendChild(errorElement);
    });
  });

  // We add the OnSelect behaviour to date pickers here after they have all been instantiated instead of when they
  // are initialized in order to be able to have min and max dates get updated when they depend on another date picker.
  // Initially this was just done by listeners on the input element, but there were issues parsing dates from text
  // in different languages, and it seemed more dependable to just use the Date given by OnSelect in the date picker.
  addOnSelectBehaviourToDatePickers(instantiatedDatePickers, datePickersWithMinMaxBoundary);
};

window.addEventListener("load", initializeDatePickerContainers);
